import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { CampaignMedium } from '@prisma/client';
import { Box, CircularProgress } from '@mui/material';
import useGetEmailTemplates from '../../../hooks/marketing/useGetEmailTemplates';
import useGetSmsTemplates from '../../../hooks/marketing/useGetSmsTemplates';
import useDeleteTemplate from '../../../hooks/marketing/useDeleteTemplate';
import useUpdateEmailTemplate from '../../../hooks/marketing/useUpdateEmailTemplate';
import useUpdateSmsTemplate from '../../../hooks/marketing/useUpdateSmsTemplate';
import FullScreenFloatingModal from '../../modal/FullScreenFloatingModal';
import { isNil } from '../../../utils/global/helper';
import useConfirm from '../../../hooks/useConfirm';
import TemplateEmbed, { TemplateEmbedData } from './TemplateEmbed';
import useStateCooldown from '../../../hooks/useStateCooldown';
import {
  MarketingEmailTemplateWithSource,
  MarketingSmsTemplateWithSource,
} from '../../../types/marketing';
import TabBar from '../../filtering/TabBar';
import { TabInt } from '../../../interfaces';
import CampaignTemplatesList from '../CampaignTemplatesList';
import useUser from '../../../hooks/global/useUser';
import { getAccessLevel } from '../../../helpers/marketing';

interface Props {
  medium: CampaignMedium;
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (
    template:
      | MarketingEmailTemplateWithSource
      | MarketingSmsTemplateWithSource
      | null,
  ) => void;
  selectedTemplate:
    | MarketingEmailTemplateWithSource
    | MarketingSmsTemplateWithSource
    | null
    | undefined;
  /// Opens straight into the editor for this template, skipping the picker
  initialEmbed?: TemplateEmbedData | null;
}

enum Tab {
  CUSTOM,
  SHARED,
  PROJECT,
}

function CampaignTemplateDesigner({
  medium,
  isOpen,
  onClose,
  onSelectTemplate,
  selectedTemplate,
  initialEmbed,
}: Props) {
  const confirm = useConfirm();
  const { user } = useUser();

  const onFetchSuccess = ({ templates }: { templates: { id: number }[] }) => {
    const isSelectedTemplateDeleted =
      selectedTemplate && !templates.some(t => t.id === selectedTemplate.id);
    if (isSelectedTemplateDeleted) {
      onSelectTemplate(null);
    }
  };

  const {
    data: {
      templates: emailTemplates = [],
      projectTemplates: emailProjectTemplates = [],
    } = {},
    refetch: refetchEmailTemplates,
    isFetching: isFetchingEmail,
    isLoading: isLoadingEmail,
  } = useGetEmailTemplates({
    enabled: isOpen && medium === 'EMAIL',
    onSuccess: medium === 'EMAIL' ? onFetchSuccess : undefined,
  });
  const {
    data: { templates: smsTemplates = [] } = {},
    refetch: refetchSmsTemplates,
    isFetching: isFetchingSms,
    isLoading: isLoadingSms,
  } = useGetSmsTemplates({
    enabled: isOpen && medium === 'SMS',
    onSuccess: medium === 'SMS' ? onFetchSuccess : undefined,
  });

  const accessLevel = getAccessLevel(user);

  const projectTemplates = React.useMemo(
    () => (medium === 'EMAIL' ? emailProjectTemplates : []),
    [emailProjectTemplates, medium],
  );
  // Widen to the element union up front — calling array methods on a union of
  // two array types is not allowed.
  const templates: (
    | (typeof emailTemplates)[number]
    | (typeof smsTemplates)[number]
  )[] = medium === 'EMAIL' ? emailTemplates : smsTemplates;

  const customTemplates = React.useMemo(() => {
    if (accessLevel === 'self') {
      return templates.filter(
        t => t.level === 'PRIVATE' && t.employeeId === user?.userId,
      );
    }

    return templates;
  }, [templates, accessLevel, user]);

  const shopTemplates = React.useMemo(() => {
    if (accessLevel === 'full') return [];
    return templates.filter(t => t.level === 'SHARED');
  }, [templates, accessLevel]);

  const refetch =
    medium === 'EMAIL' ? refetchEmailTemplates : refetchSmsTemplates;
  const isFetching = medium === 'EMAIL' ? isFetchingEmail : isFetchingSms;
  const isLoading = medium === 'EMAIL' ? isLoadingEmail : isLoadingSms;

  const [saveDisabled, setSaveDisabled] = useStateCooldown(2000);
  const [embedTemplate, setEmbedTemplate] = useState<TemplateEmbedData | null>(
    initialEmbed ?? null,
  );
  const isEmbedOnly = !!initialEmbed;

  useEffect(() => {
    if (initialEmbed) setEmbedTemplate(initialEmbed);
  }, [initialEmbed]);
  const [currentTab, setCurrentTab] = React.useState<Tab>(Tab.CUSTOM);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const onBackFromEmbed = async () => {
    if (isEmbedOnly) {
      onClose();
      return;
    }
    setEmbedTemplate(null);
    if (medium === 'EMAIL') await refetchEmailTemplates();
    else if (medium === 'SMS') await refetchSmsTemplates();
  };

  const { mutateAsync: deleteTemplate, isPending: isDeleting } =
    useDeleteTemplate({
      onSuccess: async () => {
        await refetch();
        setEmbedTemplate(null);
      },
      onError: () => {
        toast.error('Failed to delete email template');
      },
    });
  const { mutate: updateEmailTemplate } = useUpdateEmailTemplate();
  const { mutateAsync: updateSmsTemplate } = useUpdateSmsTemplate();

  const onDeleteTemplate = async () => {
    if (isDeleting || !embedTemplate) return;
    const templateId = embedTemplate?.id;
    if (isNil(templateId)) return;

    const template = customTemplates.find(t => t.id === templateId);
    if (!template) return;

    if (template.inUse) {
      toast.error(
        'This template is used in one or more campaigns or automations and cannot be deleted.',
      );
      return;
    }

    const confirmed = await confirm({
      title: 'Delete Template',
      message:
        'Are you sure you want to delete this template? This action cannot be undone.',
      isSmall: true,
      positiveText: 'Delete',
    });
    if (!confirmed) return;

    deleteTemplate({ type: medium, templateId: templateId! });
  };

  const onSave = async () => {
    if (!embedTemplate) return;
    const iframeEl = iframeRef.current;
    if (!iframeEl?.contentWindow || !embedTemplate) return;

    iframeEl.contentWindow.postMessage(
      { pocketflows: true, action: 'save' },
      '*',
    );

    setSaveDisabled(true);
  };

  React.useEffect(() => {
    if (!embedTemplate?.id) return undefined;

    const handleMessage = (event: MessageEvent) => {
      if (!event.data || !event.data.pocketflows || !event.data.id) return;
      if (medium === 'EMAIL') updateEmailTemplate(embedTemplate?.id);
      else if (medium === 'SMS') updateSmsTemplate(embedTemplate?.id);
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [embedTemplate, medium, updateEmailTemplate, updateSmsTemplate]);

  const tabs = React.useMemo(() => {
    const tempTabs: TabInt[] = [
      {
        id: Tab.CUSTOM,
        label: 'Custom Templates',
        component: (
          <CampaignTemplatesList
            type="custom"
            medium={medium}
            templates={customTemplates}
            isFetching={isFetching}
            onEmbedTemplate={setEmbedTemplate}
            selectedTemplateId={selectedTemplate?.id}
            onSelectTemplate={onSelectTemplate}
            onClose={onClose}
            onResetTabs={() => setCurrentTab(Tab.CUSTOM)}
          />
        ),
      },
      {
        id: Tab.SHARED,
        label: 'Shop Templates',
        hidden: !shopTemplates.length,
        component: (
          <CampaignTemplatesList
            type="shop"
            medium={medium}
            templates={shopTemplates}
            onEmbedTemplate={setEmbedTemplate}
            isFetching={isFetching}
            onClose={onClose}
            onResetTabs={() => setCurrentTab(Tab.CUSTOM)}
          />
        ),
      },
      {
        id: Tab.PROJECT,
        label: 'Pre-Built Templates',
        hidden: projectTemplates.length === 0,
        component: (
          <CampaignTemplatesList
            type="project"
            medium={medium}
            templates={projectTemplates}
            onEmbedTemplate={setEmbedTemplate}
            isFetching={isFetching}
            onClose={onClose}
            onResetTabs={() => setCurrentTab(Tab.CUSTOM)}
          />
        ),
      },
    ];

    return tempTabs;
  }, [
    customTemplates,
    isFetching,
    medium,
    onClose,
    onSelectTemplate,
    projectTemplates,
    selectedTemplate?.id,
    shopTemplates,
  ]);

  return (
    <FullScreenFloatingModal
      fullscreen={!!embedTemplate}
      backButton={!!embedTemplate}
      isOpen={isOpen}
      name="Design Content"
      onClose={embedTemplate ? onBackFromEmbed : onClose}
      submitButton={
        embedTemplate
          ? {
              text: 'Save',
              onClick: onSave,
              disabled: saveDisabled || isDeleting,
            }
          : undefined
      }
      secondaryButton={
        embedTemplate && !isEmbedOnly
          ? {
              text: 'Delete',
              onClick: onDeleteTemplate,
              disabled: isDeleting,
            }
          : undefined
      }>
      {!embedTemplate && !isLoading && (
        <TabBar
          tabs={tabs}
          changeTab={tab => setCurrentTab(tab as Tab) as void}
          currentTab={currentTab}
          noTopMargin
        />
      )}
      {!embedTemplate && isLoading && (
        <Box width="100%" display="flex" justifyContent="center">
          <CircularProgress size={24} color="primary" />
        </Box>
      )}
      {embedTemplate && (
        <TemplateEmbed embedTemplate={embedTemplate} iframeRef={iframeRef} />
      )}
    </FullScreenFloatingModal>
  );
}

export default CampaignTemplateDesigner;
